/**
 * Auto-advancing product media gallery.
 *
 * Built on top of the existing gallery rather than beside it:
 *  - it rotates only the slides the variant filter is currently showing,
 *    so tagged variant images are never auto-played for the wrong variant;
 *  - it moves slides through spungeVariantGallery.activateMedia, the same call
 *    the variant picker uses, so thumbnails / selected media id / zoom cleanup
 *    all behave exactly as they do on a manual switch;
 *  - it yields to the shopper: hover, focus, an open zoom, an open modal,
 *    a hidden tab, an off-screen gallery and manual navigation all pause it.
 *
 * Driven by data attributes on <media-gallery>; absent attributes = disabled.
 */
(function () {
  const HIDDEN_CLASS = 'spunge-variant-hidden';
  const MIN_INTERVAL = 1500;
  const BOUND_FLAG = 'spungeGalleryAutoplay';

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function canHover() {
    return !!(window.matchMedia && window.matchMedia('(hover: hover)').matches);
  }

  class GalleryAutoplay {
    constructor(gallery) {
      this.gallery = gallery;
      this.list = gallery.querySelector('.product__media-list');
      this.thumbnails = gallery.querySelector('[id^="GalleryThumbnails"]');

      const seconds = parseFloat(gallery.dataset.autoplayInterval);
      this.interval = Math.max(MIN_INTERVAL, (isNaN(seconds) ? 5 : seconds) * 1000);
      this.stopOnInteraction = gallery.dataset.autoplayStopOnInteraction !== 'false';

      this.timer = null;
      this.stopped = false;
      this.holds = new Set();

      this.bind();
      this.schedule();
    }

    /* ---------------------------------------------------------------- state */

    hold(reason) {
      this.holds.add(reason);
      this.clearTimer();
    }

    release(reason) {
      this.holds.delete(reason);
      this.schedule();
    }

    stop() {
      this.stopped = true;
      this.clearTimer();
    }

    clearTimer() {
      if (this.timer === null) return;
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    schedule() {
      this.clearTimer();
      if (this.stopped || this.holds.size) return;
      this.timer = window.setTimeout(() => {
        this.timer = null;
        if (this.canAdvance()) this.next();
        this.schedule();
      }, this.interval);
    }

    canAdvance() {
      if (this.stopped || this.holds.size) return false;
      // background tab
      if (document.hidden) return false;
      // a modal / drawer / quick view is open
      if (document.body.classList.contains('overflow-hidden')) return false;
      // the shopper is zoomed into an image
      if (this.gallery.querySelector('.image-magnify-full-size')) return false;
      return this.getSlides().length > 1;
    }

    /* --------------------------------------------------------------- slides */

    // Only slides the shopper can actually reach right now. Deliberately class
    // based: on desktop every inactive slide is display:none, so measuring
    // layout here would report a single slide and kill the rotation.
    getSlides() {
      if (!this.list) return [];
      return Array.from(this.list.querySelectorAll('.product__media-item[data-media-id]')).filter((slide) => {
        if (slide.classList.contains(HIDDEN_CLASS)) return false; // hidden by the variant filter
        if (slide.style.display === 'none') return false;
        if (slide.classList.contains('product__media-item--variant')) return false; // "hide variant images"
        if (slide.classList.contains('product__media-item--full')) return false; // video / 3D model
        return true;
      });
    }

    next() {
      const slides = this.getSlides();
      if (slides.length < 2) return;

      const active = this.gallery.querySelector('.product__media-item.is-active');
      const activeId = active ? active.dataset.mediaId : null;
      const index = slides.findIndex((slide) => slide.dataset.mediaId === activeId);
      // index -1 (active slide filtered out) starts the loop at the first slide
      const target = slides[(index + 1) % slides.length];
      if (!target || target.dataset.mediaId === activeId) return;

      const api = window.spungeVariantGallery;
      if (!api || typeof api.activateMedia !== 'function') return;
      api.activateMedia(this.gallery, target.dataset.mediaId, { smooth: true, source: 'autoplay' });
    }

    /* ------------------------------------------------------------ listeners */

    interacted() {
      if (this.stopOnInteraction) this.stop();
      else this.schedule();
    }

    bind() {
      // manual navigation hands control back to the shopper
      if (this.thumbnails) {
        this.thumbnails.addEventListener('click', () => this.interacted());
      }
      this.gallery.querySelectorAll('.slider-button').forEach((button) => {
        button.addEventListener('click', () => this.interacted());
      });
      if (this.list) {
        // a swipe or tap on the gallery itself (touch only - a mouse click here
        // is a zoom, which the hover hold already covers)
        this.list.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'touch' || event.pointerType === 'pen') this.interacted();
        });
      }

      if (canHover()) {
        this.gallery.addEventListener('mouseenter', () => this.hold('hover'));
        this.gallery.addEventListener('mouseleave', () => this.release('hover'));
      }

      this.gallery.addEventListener('focusin', () => this.hold('focus'));
      this.gallery.addEventListener('focusout', (event) => {
        if (this.gallery.contains(event.relatedTarget)) return;
        this.release('focus');
      });

      // something else moved the slide (variant switch, thumbnail, our own
      // advance) - restart the countdown from there
      this.gallery.addEventListener('spunge:mediaActivated', () => this.schedule());

      this.onVisibilityChange = () => {
        if (document.hidden) this.hold('tab');
        else this.release('tab');
      };
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if (window.IntersectionObserver) {
        new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.release('offscreen');
              else this.hold('offscreen');
            });
          },
          { threshold: 0.15 }
        ).observe(this.gallery);
      }
    }
  }

  function bindGallery(gallery) {
    if (!gallery || gallery.dataset[BOUND_FLAG] === '1') return;
    if (gallery.dataset.autoplay !== 'true') return;
    if (prefersReducedMotion()) return;

    const slides = gallery.querySelectorAll('.product__media-item[data-media-id]');
    if (slides.length < 2) return;

    gallery.dataset[BOUND_FLAG] = '1';
    new GalleryAutoplay(gallery);
  }

  function boot() {
    document.querySelectorAll('media-gallery[data-autoplay="true"]').forEach(bindGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
